import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface PastedFieldsContextValue {
  pastedFields: ReadonlySet<string>;
  markPasted: (names: string[]) => void;
  unmarkPasted: (name: string) => void;
  clearPasted: () => void;
}

const noop = () => {};

/**
 * Form fields filled by a paste from Excel. They stay highlighted until the
 * form is saved or the user edits them, so the paste can be reviewed before
 * saving. Outside a provider every field reads as not pasted.
 */
const PastedFieldsContext = createContext<PastedFieldsContextValue>({
  pastedFields: new Set(),
  markPasted: noop,
  unmarkPasted: noop,
  clearPasted: noop,
});

/**
 * State of the pasted fields, created by the form that owns the provider so
 * it can clear the highlight once the form is saved or cancelled.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export const usePastedFieldsState = (): PastedFieldsContextValue => {
  const [pastedFields, setPastedFields] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const markPasted = useCallback((names: string[]) => {
    setPastedFields((previous) => new Set([...previous, ...names]));
  }, []);

  const unmarkPasted = useCallback((name: string) => {
    setPastedFields((previous) => {
      if (!previous.has(name)) return previous;
      const next = new Set(previous);
      next.delete(name);
      return next;
    });
  }, []);

  const clearPasted = useCallback(() => {
    setPastedFields((previous) => (previous.size === 0 ? previous : new Set()));
  }, []);

  return useMemo(
    () => ({
      pastedFields,
      markPasted,
      unmarkPasted,
      clearPasted,
    }),
    [pastedFields, markPasted, unmarkPasted, clearPasted],
  );
};

export const PastedFieldsProvider = ({
  value,
  children,
}: PropsWithChildren<{ value: PastedFieldsContextValue }>) => (
  <PastedFieldsContext.Provider value={value}>
    {children}
  </PastedFieldsContext.Provider>
);

// oxlint-disable-next-line react-refresh/only-export-components
export const usePastedFields = () => useContext(PastedFieldsContext);

/** Whether one field was filled by a paste, and how to drop its highlight. */
// oxlint-disable-next-line react-refresh/only-export-components
export const usePastedField = (name: string) => {
  const { pastedFields, unmarkPasted } = usePastedFields();
  const isPasted = pastedFields.has(name);
  const unmark = useCallback(() => unmarkPasted(name), [name, unmarkPasted]);
  return { isPasted, unmark };
};
